import SearchPartsExtraFilters from '@/components/SearchParts/SearchPartsExtraFilters';
import AppPageHeader from '../AppPageHeader';
import {
  Row,
  Col,
  Checkbox,
  Button,
  Divider,
  Image,
  Modal,
  Popover,
} from 'antd';
import {
  FilterTypeEnum,
  ListStockItemsQuery,
  StockItem,
  StockItemType,
  StockOrderColumn,
} from '@/core/domain/stock/StockItems/StockItems.types';
import partsOrderColumns from '@/utils/functions/partsOrderColumns/partsOrderColumns';
import InfiniteTable from '../InfiniteTable';
import PartSimpliedRegisterModal from '../PartSimpliedRegisterModal';
import { useCallback, useMemo, useState } from 'react';
import { useDebounce, useDisclosure, useWindowSize } from '@/hooks';
import { useStockItems } from '@/core/domain/stock/StockItems/StockItems.hooks';
import { flatInfiniteResult } from '@/utils/request/flatInfiniteResult';
import usePartsColumns from '@/components/Operational/Inventory/Parts/usePartsColumns';
import { ColumnsType } from '../Table';
import { Check } from 'react-feather';
import { TableRowSelection } from 'antd/lib/table/interface';
import ModalLoadingOverlay from '../ModalLoadingOverlay';
import Group from '../Group';

interface Props {
  opened: boolean;
  onClose: VoidFunction;
  value?: number[];
  modeType?: 'multiple' | 'single';
  onMultipleSelect?(ids: number[], parts: StockItem[]): Promise<void> | void;
  onSingleSelect?(id: number, part: StockItem): Promise<void> | void;
  loading?: boolean;
  vehicle?: {
    id: number;
    model: string;
  };
  companyId?: number;
}

export default function WorkorderItemsSelectModal({
  opened,
  onClose,
  modeType = 'single',
  onMultipleSelect,
  onSingleSelect,
  value,
  loading,
  vehicle,
  companyId,
}: Props) {
  const [partsSelection, setPartsSelection] = useState<{
    partIds: number[];
    parts: StockItem[];
  } | null>();
  const [partModalOpened, partModalAction] = useDisclosure();

  const { isMobile } = useWindowSize();

  const initialQueryParams = useMemo(
    (): ListStockItemsQuery => ({
      companiesIds: companyId ? [companyId] : [],
      order: 'ASC',
      itemType: StockItemType.Part,
      orderColumns: StockOrderColumn.partId,
      filterType: FilterTypeEnum.free,
      active: true,
      vehicleId: undefined,
    }),
    [companyId],
  );

  const [queryParams, setQueryParams] =
    useState<ListStockItemsQuery>(initialQueryParams);

  const queryDebounce = useDebounce(queryParams, 500);

  const stockItemList = useStockItems(opened, queryDebounce);

  const stockItems = flatInfiniteResult(stockItemList.data);

  const partsColumns = usePartsColumns({
    hasSorter: true,
    showImage: true,
    enabled: opened,
  });

  const rowSelection = useMemo((): undefined | TableRowSelection<StockItem> => {
    if (modeType === 'single') return undefined;

    return {
      type: 'checkbox',
      preserveSelectedRowKeys: true,
      selectedRowKeys,
      onChange: (ids, parts) => {
        setPartsSelection({ partIds: ids as number[], parts });
      },
      getCheckboxProps: (item) => ({
        disabled: value?.includes(item.partId),
      }),
    };
  }, [modeType]);

  const columns: ColumnsType<StockItem> = useMemo(() => {
    if (modeType === 'single') {
      return [
        ...partsColumns.columns,
        {
          key: 'action',
          title: 'Ação',
          fixed: 'right',
          render: (_, row: StockItem) => (
            <Popover content="Selecionar">
              <Button
                disabled={value?.includes(row.partId)}
                shape="circle"
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: 0,
                }}
                onClick={() => {
                  if (onSingleSelect) onSingleSelect(row.partId, row);
                }}
                type="primary"
              >
                <Check size={16} style={{ margin: 0 }} />
              </Button>
            </Popover>
          ),
        },
      ];
    }

    return partsColumns.columns;
  }, [modeType, partsColumns.columns, value]);

  const selectedRowKeys = useMemo(() => {
    return [...(value || []), ...(partsSelection?.partIds || [])];
  }, [value, partsSelection]);

  const handleMultipleSubmit = useCallback(async () => {
    if (onMultipleSelect && partsSelection) {
      await onMultipleSelect(partsSelection?.partIds, partsSelection?.parts);
    }
  }, [partsSelection, onMultipleSelect]);

  const handleClose = () => {
    setPartsSelection(undefined);
    setQueryParams(initialQueryParams);
    onClose();
  };

  return (
    <Modal
      open={opened}
      onCancel={handleClose}
      width="100%"
      title={`Seleção de ${modeType === 'multiple' ? 'peças' : 'peça'}`}
      footer={modeType === 'single' ? null : undefined}
      onOk={handleMultipleSubmit}
      bodyStyle={{ position: 'relative' }}
      destroyOnClose
    >
      {loading && (
        <ModalLoadingOverlay posAbsolute messages={['Carregando...']} />
      )}
      <AppPageHeader
        searchValue={queryParams.search}
        onSearch={(value) =>
          setQueryParams((prev) => ({ ...prev, search: value }))
        }
        extraFilters={
          <SearchPartsExtraFilters setQueryParams={setQueryParams} />
        }
      />

      <Row align="middle" gutter={[12, 12]}>
        <>
          <Col>
            {!!vehicle && (
              <Checkbox
                checked={!!queryParams.vehicleId}
                onChange={({ target }) =>
                  setQueryParams((prev) => ({
                    ...prev,
                    vehicleId: target.checked ? vehicle.id : undefined,
                  }))
                }
              >
                {`Mostrar somente peças do modelo ${vehicle.model}`}
              </Checkbox>
            )}
          </Col>

          <Col flex={1}>
            {!!vehicle && (
              <Group gap={16}>
                <Checkbox
                  checked={!!queryParams.isPartReplacement}
                  onChange={({ target }) =>
                    setQueryParams((prev) => ({
                      ...prev,
                      isPartReplacement: target.checked,
                      availableStock: false,
                    }))
                  }
                >
                  Mostrar somente peças substitutas
                </Checkbox>

                <Checkbox
                  checked={!!queryParams.availableStock}
                  onChange={({ target }) =>
                    setQueryParams((prev) => ({
                      ...prev,
                      availableStock: target.checked,
                      isPartReplacement: false,
                    }))
                  }
                >
                  Mostrar peça com estoque disponível
                </Checkbox>
              </Group>
            )}
          </Col>
        </>

        <Col span={isMobile ? 24 : undefined}>
          <Button type="primary" block onClick={partModalAction.open}>
            Cadastrar peça
          </Button>
        </Col>
      </Row>

      <Divider />

      <Image.PreviewGroup>
        <InfiniteTable<StockItem>
          columns={columns}
          onChange={(_p, _f, sorter) => {
            if (!Array.isArray(sorter)) {
              partsOrderColumns(sorter, setQueryParams);
            }
          }}
          dataSource={stockItems}
          rowKey={(row) => row.partId}
          loading={stockItemList.isFetching || partsColumns.isFetching}
          hasNextPage={stockItemList.hasNextPage}
          fetchNextPage={stockItemList.fetchNextPage}
          rowSelection={rowSelection}
        />
      </Image.PreviewGroup>

      <PartSimpliedRegisterModal
        open={partModalOpened}
        onCancel={partModalAction.close}
        onCallback={({ part }) => {
          setQueryParams({ ...initialQueryParams, search: part.name });
        }}
      />
    </Modal>
  );
}
